/**
 * Assigned Services filter — shared predicate (issue #712).
 *
 * `AssignedServicesTable` (row display) and the Project Detail route (which
 * feeds `ExportServicesDialog`) both need to apply the exact same
 * Team + Milestone + free-text filter to the same assignment set, so Export
 * always matches what the table currently shows. Prior to this module the
 * predicate lived only inside `AssignedServicesTable`, so the route's Export
 * dialog was handed the full, unfiltered assignment list (#712). Extracting
 * the predicate — and the small id->name/team map builders it depends on —
 * into this pure module lets both call sites share one implementation
 * instead of risking two hand-written copies drifting apart.
 */

import type { ProjectMilestone, ProjectOutcome, ServiceAssignment } from '../graphql/types';

/** The three independently-optional filters, AND-composed. */
export interface AssignedServicesFilterState {
  textFilter: string;
  teamFilter: string;
  milestoneFilter: string;
}

/** No filter active — the identity filter state. */
export const EMPTY_ASSIGNED_SERVICES_FILTERS: AssignedServicesFilterState = {
  textFilter: '',
  teamFilter: '',
  milestoneFilter: '',
};

export interface AssignedServicesFilterMaps {
  /** serviceId -> owning teamId, or null/undefined when unresolved. */
  serviceTeamIdMap: Map<string, string | null>;
  /** serviceId -> display name, used for free-text matching. */
  serviceNameMap: Map<string, string>;
  /** milestoneId -> display name, used for free-text matching. */
  milestoneNameMap: Map<string, string>;
  /** outcomeId -> display name, used for free-text matching. */
  outcomeNameMap: Map<string, string>;
}

/**
 * Resolve a display name for a service id, falling back to a truncated id
 * when the id is not present in the name map (e.g. the service was deleted,
 * or the caller only has a partial map). Mirrors the fallback previously
 * defined locally in `AssignedServicesTable`.
 */
export function resolveAssignedServiceName(
  serviceId: string,
  nameMap: Map<string, string>
): string {
  return nameMap.get(serviceId) ?? serviceId.slice(0, 16) + (serviceId.length > 16 ? '…' : '');
}

/** Build a milestoneId -> name lookup from a project's milestone list. */
export function buildMilestoneNameMap(milestones: ProjectMilestone[]): Map<string, string> {
  return new Map(milestones.map((m) => [m.entityId, m.name]));
}

/** Build an outcomeId -> name lookup from a project's outcome list. */
export function buildOutcomeNameMap(outcomes: ProjectOutcome[]): Map<string, string> {
  return new Map(outcomes.map((o) => [o.entityId, o.name]));
}

/**
 * Apply the Team + Milestone + free-text filter to an assignment set.
 * All three filters are AND-composed — an assignment must pass every active
 * filter (an empty string/no team selected means that filter is inactive).
 */
export function filterAssignments(
  assignments: ServiceAssignment[],
  filters: AssignedServicesFilterState,
  maps: AssignedServicesFilterMaps
): ServiceAssignment[] {
  const { textFilter, teamFilter, milestoneFilter } = filters;
  const { serviceTeamIdMap, serviceNameMap, milestoneNameMap, outcomeNameMap } = maps;
  const textLower = textFilter.toLowerCase();

  return assignments.filter((a) => {
    if (teamFilter && serviceTeamIdMap.get(a.serviceId) !== teamFilter) return false;
    if (milestoneFilter && a.milestoneId !== milestoneFilter) return false;
    if (!textLower) return true;
    const svcName = resolveAssignedServiceName(a.serviceId, serviceNameMap).toLowerCase();
    const msName = (milestoneNameMap.get(a.milestoneId) ?? '').toLowerCase();
    const ocName = (outcomeNameMap.get(a.outcomeId) ?? '').toLowerCase();
    return svcName.includes(textLower) || msName.includes(textLower) || ocName.includes(textLower);
  });
}
